import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import DemandPage from "./pages/Demandpage.jsx";

import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";

import MultimeterRelay1Alarms from "./pages/alarms/MultimeterRelay1Alarms.jsx";
import MultimeterRelay2Alarms from "./pages/alarms/MultimeterRelay2Alarms.jsx";

import ReactiveSettingsPage from "./pages/ReactiveSettingsPage.jsx";
import ReactiveStepsPage from "./pages/ReactiveStepsPage.jsx";

// Customer pages
import GatewayList from "./pages/GatewayList";
import DeviceList from "./pages/DeviceList";
import Layout from "./Layout/Layout";
import VoltagesPage from "./pages/VoltagesPage";
import CurrentsPage from "./pages/CurrentsPage";
import ActivePowerPage from "./pages/ActivePowerPage";
import ReactivePowerPage from "./pages/ReactivePowerPage";
import ReactiveHarmonicsPage from "./pages/ReactiveHarmonicsPage";
import ApparentPowerPage from "./pages/ApparentPowerPage";
import CosfiPage from "./pages/CosfiPage";
import UnbalancesPage from "./pages/UnbalancesPage";
import PowerFactorsPage from "./pages/PowerFactorsPage";
import RatiosPage from "./pages/RatiosPage.jsx";
import RealTimePage from "./pages/RealtimePage.jsx";
import Relay1Alarms from "./pages/alarms/Relay1Alarms.jsx";
import Relay2Alarms from "./pages/alarms/Relay2Alarms.jsx";
import DeviceMapPage from "./pages/DeviceMapPage.jsx";
import MimicPage from "./pages/MimicPage.jsx";
import HierarchyPage from "./pages/HierarchyBuilder.jsx";
import AnalyzerEnergyDashboard from "./pages/energies/AnalyzerEnergyDashboard.jsx";
import ReactiveEnergyDashboard from "./pages/energies/ReactiveEnergyDashboard.jsx";
import VirtualEnergyDashboard from "./pages/energies/VirtualEnergyDashboard.jsx";
import GrupArgeEnergyDashboard from "./pages/energies/GrupArgeEnergyDashboard.jsx";
import InductivePowerPage from "./pages/InductivePowerPage.jsx";
import CapacitivePowerPage from "./pages/CapacitivePowerPage.jsx";
import VoltampermeVoltagesPage from "./pages/VoltampermeVoltagesPage.jsx";
import VoltampermeCurrentsPage from "./pages/VoltampermeCurrentsPage.jsx";
import DemoDashboard from "./pages/demo/DemoDashboard";


// Admin pages
import AdminCustomers from "./pages/AdminCustomers";
import AdminGateways from "./pages/AdminGateways";
import AdminDevices from "./pages/AdminDevices";

import { useTranslation } from "react-i18next";

function App() {
  const { t, i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const [role, setRole] = useState(localStorage.getItem("role"));
  const location = useLocation();

  React.useEffect(() => {
    document.title = t("appTitle");
  }, [t, i18n.language]);

  const handleLogin = () => {
    setIsLoggedIn(true);
    setRole(localStorage.getItem("role"));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    setIsLoggedIn(false);
    setRole(null);
  };

  // 401 yanıtlarında authFetch'in dispatch ettiği event'i dinle
  useEffect(() => {
    const onAuthLogout = () => handleLogout();
    window.addEventListener("auth:logout", onAuthLogout);

    // Başka bir sekmede token silinirse de yakala
    const onStorageChange = (e) => {
      if (e.key === "token" && !e.newValue) {
        handleLogout();
      }
    };
    window.addEventListener("storage", onStorageChange);

    return () => {
      window.removeEventListener("auth:logout", onAuthLogout);
      window.removeEventListener("storage", onStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Header gösterimi
  const showHeader =
    isLoggedIn &&
    !location.pathname.startsWith("/dashboard/") &&
    location.pathname !== "/login" &&
    location.pathname !== "/reports";

  return (
    <>
      {showHeader && <Header />}

      <Routes>
        {/* 🔹 Login */}
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/demo" element={<DemoDashboard />} />


        {isLoggedIn ? (
          role === "admin" ? (
            <>
              {/* 👑 Admin */}
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route
                path="/admin/customers/:customerId/gateways"
                element={<AdminGateways />}
              />
              <Route
                path="/admin/gateways/:gatewayId/devices"
                element={<AdminDevices />}
              />
              <Route path="/map" element={<DeviceMapPage />} />

              {/* 🔹 Admin cihaz dashboard */}
              <Route path="/admin/devices/:deviceId" element={<Layout />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={`${localStorage.getItem("deviceType")}/real-time`}
                    />
                  }
                />
                {/* 🔹 Multimeter (PMA-03) sayfaları */}
                <Route
                  path="multimeter/real-time"
                  element={<RealTimePage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/voltages"
                  element={<VoltagesPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/currents"
                  element={<CurrentsPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/active-power"
                  element={<ActivePowerPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/reactive-power"
                  element={<ReactivePowerPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/apparent-power"
                  element={<ApparentPowerPage deviceType="multimeter" />}
                />

                <Route
                  path="multimeter/cosfi"
                  element={<CosfiPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/ratios"
                  element={<RatiosPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/demand"
                  element={<DemandPage deviceType="multimeter" />}
                />
                <Route path="multimeter/alarms/relay1-mm" element={<MultimeterRelay1Alarms />} />
                <Route path="multimeter/alarms/relay2-mm" element={<MultimeterRelay2Alarms />} />

                {/* 🔹 Grup Arge sayfaları */}
                <Route path="gruparge/real-time" element={<RealTimePage deviceType="gruparge" />} />
                <Route path="gruparge/voltages" element={<VoltagesPage deviceType="gruparge" />} />
                <Route path="gruparge/currents" element={<CurrentsPage deviceType="gruparge" />} />
                <Route path="gruparge/cosfi" element={<CosfiPage deviceType="gruparge" />} />
                <Route path="gruparge/ratios" element={<RatiosPage deviceType="gruparge" />} />
                <Route path="gruparge/active-power" element={<ActivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/inductive-power" element={<InductivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/capacitive-power" element={<CapacitivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/energies" element={<GrupArgeEnergyDashboard deviceType="gruparge" />} />

                {/* 🔹 Voltampermetre sayfaları (admin) */}
                <Route path="voltampermetre/real-time" element={<RealTimePage deviceType="voltampermetre" />} />
                <Route path="voltampermetre/voltages"  element={<VoltampermeVoltagesPage />} />
                <Route path="voltampermetre/currents"  element={<VoltampermeCurrentsPage />} />

                <Route
                  path="analyzer/real-time"
                  element={<RealTimePage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/real-time"
                  element={<RealTimePage deviceType="reactive" />}
                />

                {/* Diğer sayfalar */}
                <Route
                  path="analyzer/voltages"
                  element={<VoltagesPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/voltages"
                  element={<VoltagesPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/harmonics"
                  element={<ReactiveHarmonicsPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/harmonics"
                  element={<ReactiveHarmonicsPage deviceType="reactive" />}
                />
                <Route path="reactive/steps" element={<ReactiveStepsPage />} />
                <Route
                  path="analyzer/currents"
                  element={<CurrentsPage deviceType="analyzer" />}
                />
                <Route
                  path="analyzer/demand"
                  element={<DemandPage deviceType="analyzer" />}
                />

                <Route
                  path="reactive/currents"
                  element={<CurrentsPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/active-power"
                  element={<ActivePowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/active-power"
                  element={<ActivePowerPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/reactive-power"
                  element={<ReactivePowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/reactive-power"
                  element={<ReactivePowerPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/apparent-power"
                  element={<ApparentPowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/apparent-power"
                  element={<ApparentPowerPage deviceType="reactive" />}
                />

                <Route
                  path="analyzer/cosfi"
                  element={<CosfiPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/cosfi"
                  element={<CosfiPage deviceType="reactive" />}
                />

                <Route
                  path="analyzer/unbalances"
                  element={<UnbalancesPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/unbalances"
                  element={<UnbalancesPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/power-factors"
                  element={<PowerFactorsPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/power-factors"
                  element={<PowerFactorsPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/ratios"
                  element={<RatiosPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/ratios"
                  element={<RatiosPage deviceType="reactive" />}
                />
                <Route path="analyzer/alarms/relay1" element={<Relay1Alarms />} />
                <Route path="analyzer/alarms/relay2" element={<Relay2Alarms />} />
                <Route path="reactive/alarms/relay1" element={<Relay1Alarms />} />
                <Route path="reactive/alarms/relay2" element={<Relay2Alarms />} />
              </Route>

              <Route path="*" element={<Navigate to="/admin/customers" />} />
            </>
          ) : (
            <>
              {/* 👤 Customer */}
              <Route path="/" element={<GatewayList />} />
              <Route path="/map" element={<DeviceMapPage />} />
              <Route
                path="/gateways/:gatewayId/devices"
                element={<DeviceList />}
              />
              <Route path="/mimic" element={<MimicPage />} />
              <Route path="/hierarchy" element={<HierarchyPage />} />

              {/* 🔹 Fiziksel cihaz dashboard */}
              <Route path="/devices/:deviceId" element={<Layout />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={`${localStorage.getItem("deviceType")}/real-time`}
                    />
                  }
                />
                {/* 🔹 Multimeter (PMA-03) sayfaları */}
                <Route
                  path="multimeter/real-time"
                  element={<RealTimePage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/voltages"
                  element={<VoltagesPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/currents"
                  element={<CurrentsPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/active-power"
                  element={<ActivePowerPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/reactive-power"
                  element={<ReactivePowerPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/apparent-power"
                  element={<ApparentPowerPage deviceType="multimeter" />}
                />

                <Route
                  path="multimeter/cosfi"
                  element={<CosfiPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/ratios"
                  element={<RatiosPage deviceType="multimeter" />}
                />
                <Route
                  path="multimeter/demand"
                  element={<DemandPage deviceType="multimeter" />}
                />

                <Route path="multimeter/alarms/relay1-mm" element={<MultimeterRelay1Alarms />} />
                <Route path="multimeter/alarms/relay2-mm" element={<MultimeterRelay2Alarms />} />

                {/* 🔹 Grup Arge sayfaları */}
                <Route path="gruparge/real-time" element={<RealTimePage deviceType="gruparge" />} />
                <Route path="gruparge/voltages" element={<VoltagesPage deviceType="gruparge" />} />
                <Route path="gruparge/currents" element={<CurrentsPage deviceType="gruparge" />} />
                <Route path="gruparge/cosfi" element={<CosfiPage deviceType="gruparge" />} />
                <Route path="gruparge/ratios" element={<RatiosPage deviceType="gruparge" />} />
                <Route path="gruparge/active-power" element={<ActivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/inductive-power" element={<InductivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/capacitive-power" element={<CapacitivePowerPage deviceType="gruparge" />} />
                <Route path="gruparge/energies" element={<GrupArgeEnergyDashboard deviceType="gruparge" />} />

                {/* 🔹 Voltampermetre sayfaları (customer) */}
                <Route path="voltampermetre/real-time" element={<RealTimePage deviceType="voltampermetre" />} />
                <Route path="voltampermetre/voltages"  element={<VoltampermeVoltagesPage />} />
                <Route path="voltampermetre/currents"  element={<VoltampermeCurrentsPage />} />

                {/* Analyzer + Reactive */}
                <Route
                  path="analyzer/real-time"
                  element={<RealTimePage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/real-time"
                  element={<RealTimePage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/voltages"
                  element={<VoltagesPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/voltages"
                  element={<VoltagesPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/harmonics"
                  element={<ReactiveHarmonicsPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/harmonics"
                  element={<ReactiveHarmonicsPage deviceType="reactive" />}
                />
                <Route path="reactive/steps" element={<ReactiveStepsPage />} />
                <Route
                  path="analyzer/currents"
                  element={<CurrentsPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/currents"
                  element={<CurrentsPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/active-power"
                  element={<ActivePowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/active-power"
                  element={<ActivePowerPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/reactive-power"
                  element={<ReactivePowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/reactive-power"
                  element={<ReactivePowerPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/apparent-power"
                  element={<ApparentPowerPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/apparent-power"
                  element={<ApparentPowerPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/demand"
                  element={<DemandPage deviceType="analyzer" />}
                />


                <Route path="reactive/settings" element={<ReactiveSettingsPage />} />

                <Route
                  path="analyzer/cosfi"
                  element={<CosfiPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/cosfi"
                  element={<CosfiPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/energies"
                  element={<AnalyzerEnergyDashboard deviceType="analyzer" />}
                />
                <Route
                  path="reactive/energies"
                  element={<ReactiveEnergyDashboard deviceType="reactive" />}
                />
                <Route
                  path="analyzer/unbalances"
                  element={<UnbalancesPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/unbalances"
                  element={<UnbalancesPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/power-factors"
                  element={<PowerFactorsPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/power-factors"
                  element={<PowerFactorsPage deviceType="reactive" />}
                />
                <Route
                  path="analyzer/ratios"
                  element={<RatiosPage deviceType="analyzer" />}
                />
                <Route
                  path="reactive/ratios"
                  element={<RatiosPage deviceType="reactive" />}
                />
                <Route path=":deviceType/alarms/relay1" element={<Relay1Alarms />} />
                <Route path=":deviceType/alarms/relay2" element={<Relay2Alarms />} />
              </Route>

              {/* 🔹 Sanal cihaz dashboard */}
              <Route path="/dashboard/:virtualId" element={<Layout />}>
                <Route index element={<Navigate to="virtual/voltages" />} />
                <Route
                  path="virtual/voltages"
                  element={<VoltagesPage deviceType="virtual" />}
                />
                <Route
                  path="virtual/currents"
                  element={<CurrentsPage deviceType="virtual" />}
                />
                <Route
                  path="virtual/active-power"
                  element={<ActivePowerPage deviceType="virtual" />}
                />
                <Route
                  path="virtual/reactive-power"
                  element={<ReactivePowerPage deviceType="virtual" />}
                />
                <Route
                  path="virtual/apparent-power"
                  element={<ApparentPowerPage deviceType="virtual" />}
                />
                <Route
                  path="virtual/energies"
                  element={<VirtualEnergyDashboard deviceType="virtual" />}
                />
              </Route>

              <Route path="*" element={<Navigate to="/" />} />
            </>
          )
        ) : (
          <Route path="*" element={<Navigate to="/login" />} />
        )}
      </Routes>
    </>
  );
}

export default App;
